import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { BehaviorSubject, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import { NotificationsService } from '@shared/service/notifications.service';
import { LoaderService } from '@shared/service/loader.service';
import { UserService } from '@shared/service/user.service';
import { AuthApiService } from '@api/auth-api/auth-api.service';
import { TokenStore } from '@shared/store/token.store';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly _storeToken = inject(TokenStore);
  private readonly _user = inject(UserService);
  private readonly _n = inject(NotificationsService);
  private readonly _loader = inject(LoaderService);
  private readonly _api = inject(AuthApiService);

  private isRequestingToken = false;
  private newTokenArrived = new BehaviorSubject<string | boolean>(null);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any> | any> {
    request = this.setTokenToRequest(request);
    return next.handle(request).pipe(
      catchError(err => {
        if (err instanceof HttpErrorResponse) {
          if (err.status === 401 && !request.url.includes('refreshToken')) {
            return this.handle401Error(request, next, err);
          }

          if (err.status === 401 && request.url.includes('refreshToken')) {
            this.proceedLogout();
            return of(null);
          }

          if (err.status === 403 && err.error && err.error.error === 'Concurrent login') {
            this.proceedLogout(true);
            return of(null);
          } else if (err.status === 403) {
            this._n.notify('error', '', 'You dont have permission to do this action');
          }

          return throwError(err);
        } else {
          return of(null);
        }
      })
    );
  }

  private handle401Error(request, next, err) {
    if (this.isRequestingToken) {
      return this.newTokenArrived.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          if (token === false) {
            this._loader.hide();
            return throwError(err);
          } else {
            return next.handle(this.setTokenToRequest(request, token as string));
          }
        })
      );
    } else {
      this.isRequestingToken = true;
      this.newTokenArrived.next(null);

      return this._api.refreshToken(this._storeToken.refreshToken).pipe(
        map(res => {
          this._user.setUser(res.data, false);
          return res.data.token;
        }),
        switchMap(token => {
          this.newTokenArrived.next(token);
          this.isRequestingToken = false;
          return next.handle(this.setTokenToRequest(request, token));
        }),
        catchError(error => {
          this.proceedLogout();
          return throwError(() => new Error(error));
        })
      );
    }
  }

  private proceedLogout(concurrentLogin = false) {
    this.newTokenArrived.next(false);
    this.isRequestingToken = false;
    this._user.logoutActions();
    if (concurrentLogin) {
      this._n.notify('warn', '', 'Somebody else logged into your account', 'public');
    }
  }

  private setTokenToRequest(request: any, token = this._storeToken.token) {
    if (!token) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }
}
